import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { rem } from 'polished'
import styled from 'styled-components'

import ModalContext from 'contexts/ModalContext'
import Clickable from 'components/Common/Clickable'
import Modal from 'LuxKit/Modal/Modal'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import { mediaQueryUp } from 'components/utils/breakpoint'
import useCruiseCabinDescriptions from 'hooks/Cruise/useCruiseCabinDescriptions'
import useCruiseCabinTypeLabels from 'hooks/Cruise/useCruiseCabinTypeLabels'
import { getCabinCategories } from 'lib/cruises/cruiseUtils'
import zIndex from 'styles/tools/z-index'
import variables, { containerLarge } from 'styles/vendor/bootstrap/variables'

const HEADER_FLOATING_OFFSET_CSS_VARIABLE = '--app-header-floating-offset'

const DepartureHeader = styled.div<{ $isSticky: boolean }>`
  display: none;

  ${mediaQueryUp.desktop} {
    position: sticky;
    top: ${props => props.theme.layout.headerFloatingOffset};
    z-index: ${zIndex.stickySubHeader - 1};
    display: block;
    padding-block: ${rem(16)};
    width: ${props => props.$isSticky ? '100vw' : 'auto'};
    margin-inline: ${props => props.$isSticky ? 'calc(50% - 50vw)' : 0};
    border-radius: ${props => props.$isSticky ? 0 : props.theme.borderRadius.M};
    background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
    box-shadow: ${props => props.$isSticky ? props.theme.shadow.bottom.small : 'none'};
  }
`

const DepartureGrid = styled.div<{ $isSticky: boolean }>`
  ${mediaQueryUp.desktop} {
    max-width: ${props => props.$isSticky ? containerLarge : 'none'};
    margin-inline: auto;
    padding-inline: ${props => props.$isSticky ? rem(variables.gutterWidth / 2) : 0};
    display: grid;
    grid-template-columns: minmax(${rem(280)}, 1.35fr) minmax(0, 4fr) ${rem(80)};
    column-gap: ${rem(20)};
    align-items: center;
  }
`

const CabinCategories = styled.div<{ $categoryCount: number }>`
  display: grid;
  grid-template-columns: repeat(${props => props.$categoryCount}, minmax(${rem(130)}, 1fr));
  column-gap: ${rem(20)};
`

const CabinCategory = styled.div`
  display: flex;
  align-items: center;
`

const CabinCategoryContent = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(4)};
`

const DepartureCategoryContent = styled(CabinCategoryContent)`
  padding-inline: ${rem(20)};
`

interface Props {
  offer: App.CruiseOffer
}

interface CabinCategoryHeaderProps {
  category: string
  label: string
  onOpen: (category: string) => void
}

function CabinCategoryHeader({ category, label, onOpen }: CabinCategoryHeaderProps) {
  const intl = useIntl()
  const handleClick = useCallback(() => {
    onOpen(category)
  }, [category, onOpen])
  const cabinDetailsLabel = useMemo(() => intl.formatMessage(
    {
      id: 'rPGeQsG-vmZEGa3GSyQz',
      defaultMessage: 'View {category} cabin details',
      description: 'Accessible label for the button that opens a cruise cabin category description',
    },
    { category: label },
  ), [intl, label])

  return <CabinCategory>
    <CabinCategoryContent>
      <Heading variant="heading6" colour="neutral-two">
        {label}
      </Heading>
      <Clickable onClick={handleClick} aria-label={cabinDetailsLabel}>
        <LineInfoCircleIcon size={16} colour="neutral-two" />
      </Clickable>
    </CabinCategoryContent>
  </CabinCategory>
}

function CruiseDepartureCabinHeader({ offer }: Props) {
  const headerRef = useRef<HTMLDivElement>(null)
  const [isSticky, setIsSticky] = useState(false)
  const showModal = useContext(ModalContext)
  const cabinTypeLabels = useCruiseCabinTypeLabels()
  const cabinDescriptions = useCruiseCabinDescriptions()
  const categories = useMemo(
    () => getCabinCategories(offer.cruiseType).map(({ category }) => category.toLowerCase()),
    [offer.cruiseType],
  )

  const openCabinInfoModal = useCallback((category: string) => {
    showModal(<Modal title={cabinTypeLabels[category] ?? category}>
      <BodyText variant="medium">
        {cabinDescriptions[category]}
      </BodyText>
    </Modal>)
  }, [cabinDescriptions, cabinTypeLabels, showModal])

  useEffect(() => {
    const updateStickyState = () => {
      const headerTop = headerRef.current?.getBoundingClientRect().top
      const stickyOffset = Number.parseFloat(
        window.getComputedStyle(document.documentElement)
          .getPropertyValue(HEADER_FLOATING_OFFSET_CSS_VARIABLE),
      )
      const nextIsSticky = headerTop !== undefined && headerTop <= stickyOffset

      setIsSticky(currentIsSticky => currentIsSticky === nextIsSticky ? currentIsSticky : nextIsSticky)
    }

    updateStickyState()
    window.addEventListener('scroll', updateStickyState)
    window.addEventListener('resize', updateStickyState)

    return () => {
      window.removeEventListener('scroll', updateStickyState)
      window.removeEventListener('resize', updateStickyState)
    }
  }, [])

  return <DepartureHeader ref={headerRef} $isSticky={isSticky}>
    <DepartureGrid $isSticky={isSticky}>
      <CabinCategory>
        <DepartureCategoryContent>
          <Heading variant="heading6">
            <FormattedMessage
              id="bXdKU4g0bBoAuEZrm015"
              defaultMessage="Departure"
              description="Column heading for cruise departure dates in the desktop price list"
            />
          </Heading>
        </DepartureCategoryContent>
      </CabinCategory>
      <CabinCategories $categoryCount={categories.length}>
        {categories.map((category) => <CabinCategoryHeader
          key={category}
          category={category}
          label={cabinTypeLabels[category] ?? category}
          onOpen={openCabinInfoModal}
        />)}
      </CabinCategories>
      <div aria-hidden />
    </DepartureGrid>
  </DepartureHeader>
}

export default CruiseDepartureCabinHeader
